<template>
  <div style="display: grid;">
    <button @click="openModal()" class="btn-outline btn-tertiary"><i class="fa-light fa-arrow-up-from-bracket"></i>Import CSV</button>
    
    <!-- validation alert -->
    <alert-modal v-if="isAlertOpen">
      <span><i class="fa-solid fa-circle-check"></i>Bulk allocation complete: : {{ this.countCreated }} new, {{ this.countExisting }} existing</span>
    </alert-modal>

    <!-- modal bulk import -->
    <div class="modal-container" v-if="isModalOpen">
      <div class="modal-content">
        <h1>Bulk Import Orders<i @click="closeModal()" class="fa-regular fa-xmark"></i></h1>
        <span style="font-weight: normal; color: #717182;">Download the CSV template, fill it with order data, and upload to import multiple orders at once.</span>
        <div class="information-container">
          <i class="fa-regular fa-file-lines"></i>
          <div>
            <span>CSV Template</span>
            <span>Download template with sample data and required columns</span>
          </div>
          <button class="download-btn btn btn-outline" @click="exportCSVTemplate"><i class="fa-regular fa-arrow-down-to-bracket"></i>Download Template</button>
        </div>
        <div
          v-if="orders.length == 0"
          class="drop-zone"
          :class="{ hover: isDragOver }"
          @dragover.prevent
          @dragenter.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="onDrop"
        >
          <i class="fa-regular fa-arrow-up-from-bracket"></i>
          <span>Drag and drop your CSV file here, or click to browse</span>
          <span>Supports CSV files with order data</span>
          <div>
            <button @click="importCSVFile" type="file" class="btn-outline btn-dark"><i class="fa-regular fa-arrow-up-from-bracket"></i> Select CSV file</button>
          </div>
        </div>
        <div v-else>
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 1.25rem; font-weight: bold; color: black;">
            <span>Preview ({{ orders.length }} orders)</span>
            <div style="display: flex; gap: 1rem;">
              <span class="btn-outline button-primary btn-outline-light btn" :style="{cursor: 'default', 'pointer-events': 'none', 'background-color': orders.length > 0 ? '#f0fdf4':'white', color: orders.length > 0 ? '#008236':''}">{{ orders.length }} valid</span>

              <button class="btn-outline-light btn" @click="orders = []"><i class="fa-regular fa-xmark"></i> Clear</button>
            </div>
          </div>
          <b-table responsive
            class="table-header-border"
            style="margin-bottom:0; margin-top: 2rem;"
            :items="orders"
            :fields="fields"
            primary-key="id"
          >

          <template #head(index)>
            <span>#</span>
          </template>

          <template #cell(index)="data">
            <span>{{ data.index + 1 }}</span>
          </template>

          <template #cell(status)="data">
            <i style="color: #d4183d;" v-if="data.item.amount == null || data.item.amount.trim() == ''" class="fa-regular fa-circle-exclamation"></i>
            <i style="color: #00a63e;" v-else class="fa-regular fa-circle-check"></i>
          </template>

          <template #cell(clientnumber)="data">
            <span v-if="data.item.clientNumber != ''">
              {{ !checkAllocationClient(data.item.clientNumber) ? generateClientNumber() : data.item.clientNumber }}
            </span>
            <span style="color: #155dfc;" v-else>{{ generateClientNumber() }}</span>
          </template>

          <template #cell(amount)="data">
            <span>
              €{{ data.item.amount }}
            </span>
          </template>

          <template #cell(date)="data">
            <span>
              {{ data.item.date }}
            </span>
          </template>

          <template #cell(duedate)="data">
            <span class="btn-empty">
              {{ data.item.dueDate }}
            </span>
          </template>

          <template #cell(reference)="data">
            <span v-if="data.item.reference != ''">
              {{ !checkAllocation(data.item.reference) ? generateReference() : data.item.reference }}
            </span>
            <span style="color: #155dfc;" v-else>{{ generateReference() }}</span>
          </template>

          <template #cell(email)="data">
            <span v-if="data.item.email != null && data.item.email.trim() != ''">{{ data.item.email }}</span>
            <span v-else>—</span>
          </template>

          </b-table>
          <hr>
          <div style="display: flex; justify-content: end; align-items: center; gap: 1rem;">
            <button class="btn-outline-light btn" @click="closeModal">Cancel</button>
            <button v-if="orders.length > 0" class="btn-outline btn-dark" style="background-color: rgb(21, 93, 252);border: none;" @click="submit"> <i class="fa-regular fa-arrow-up-from-bracket"></i>Import {{ orders.length }} Orders</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import alertModal from './alertModal.vue';
export default {
  components: { alertModal },
  name: 'ImportOrderModal',
  emits: ['update:refresh'],
  data(){
    return {
      fields: [
        { key: 'index', thClass: 'text-center align-middle border-bottom border-top border-left', tdClass: 'text-center align-middle border-left border-bottom'},
        { key: 'status', label: 'Status', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left border-bottom'},
        { key: 'clientnumber', label: 'Client Number', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left text-nowrap border-bottom'},
        { key: 'reference', label: 'Order Reference', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left text-nowrap border-bottom'},
        { key: 'amount', label: 'Amount', thClass: 'text-right align-middle border-bottom border-top', tdClass: 'align-middle text-left text-nowrap border-bottom'},
        { key: 'email', label: 'Email', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left text-nowrap border-bottom'},
        { key: 'date', label: 'Date', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left text-nowrap border-bottom'},
        { key: 'duedate', label: 'Payment Term', thClass: 'text-left align-middle border-bottom border-top border-right', tdClass: 'align-middle text-left text-nowrap border-bottom border-right'},
      ],
      isModalOpen: false,
      isAlertOpen: false,
      countCreated: 0,
      countExisting: 0,
      orders : [],
      isDragOver: false,
      reference: null,
      clientNumber: null
    }
  },
  mounted() {
    window.addEventListener('dragover', e => e.preventDefault())
    window.addEventListener('drop', e => e.preventDefault())
  },
  methods: {
    openModal (){
      this.isModalOpen = true
    },
    closeModal (){
      this.isModalOpen = false
      this.countExisting = 0
      this.countCreated = 0
      this.orders = []
    },
    exportCSVTemplate (){
      let firstLine = 'clientNumber;orderReference;amount;email;transactionDate;expectedPaymentTerm' + '\r\n'

      let line = ''
      line += 'CLI-24943402;ORD-792803-DBT;274.13; customer-4reet0@example.com;'+ new Date().toLocaleDateString()+';Instant'  + '\r\n'
      line += 'CLI-35394207;ORD-792803-DXU;643.02;customer-o6ni3z@example.com;'+ new Date().toLocaleDateString()+';T+7' + '\r\n'
      line += ';;546.61;;'+ new Date().toLocaleDateString()+';T+14' + '\r\n'

      let data = firstLine + line
      let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
      let downloadLink = document.createElement('a')
      let today = moment()
      let fileName = 'import_CSV_template_' + today.unix() + '.csv'

      downloadLink.href = linkSource
      downloadLink.download = fileName
      downloadLink.click()
    },
    onDrop (event) {
      this.isDragOver = false
      const file = event.dataTransfer.files[0]
      this.readCSVFile(file)
    },
    readCSVFile (file) {
      if (!file || !file.name.endsWith('.csv')) return

      const reader = new FileReader()
      reader.onload = (e) => {
        const csvText = e.target.result
        const fileContent = csvText.trim().split('\r\n').filter(item => item && item !== 'clientNumber;orderReference;amount;email;transactionDate;expectedPaymentTerm')

        fileContent.forEach(el => {
          let elements = el.split(';')
          let clientNumber = this.sanitize(elements[0])
          let reference = this.sanitize(elements[1])
          let amount = elements[2]
          let email = elements[3]
          let date = elements[4]
          if (date == null || date.trim() == '') date = new Date().toLocaleDateString
          
          let dueDate = elements[5]
          if (dueDate == null || dueDate.trim() == '') dueDate = 'Instant'
          
          let data = {
            clientNumber: clientNumber,
            reference: reference,
            amount: amount,
            email: email,
            date: date,
            dueDate: dueDate,
            executionDate: dueDate == 'Instant' ? 0 : Number(dueDate.replace('T+', ''))
          } 
          
          this.orders.push(data)
        })
      }
      reader.readAsText(file)
    },
    importCSVFile () {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.csv'

      input.addEventListener('change', (event) => {
        const file = event.target.files[0]
        this.readCSVFile(file)
      })

      input.click()
    },
    async submit (){
      for (const order of this.orders) {
        
        if(order.amount == null) continue

        const dueDate = new Date(order.date)
        dueDate.setDate(dueDate.getDate() + order.executionDate)
        order.executionDate = dueDate

        order.reference = order.reference.trim() == '' || order.reference == null ? this.reference : order.reference

        order.clientNumber = order.clientNumber.trim() == '' || order.clientNumber == null ? this.clientNumber : order.clientNumber
        
        const groupRes = await this.$engine.services.payments.createGroup(order.clientNumber)
       
        let invoice = {
          "InvoiceIdentifier": order.reference,
          "PaymentDate": order.date,
          "ExecutionDate": order.executionDate,
          "Amount": order.amount,
          'CustomerEmail': order.email,
          "Type": 'Standard',
          "Description": null,
          "Reference1": order.clientNumber,
          "VIBAN": groupRes.data.VIBAN
        }
    
        await this.$engine.services.payments.createInvoice(invoice)
      }

      this.$emit('update:refresh')

      this.closeModal()
    },
    sanitize (value) {
      if (typeof value !== 'string') return ''

      return value
        .trim()
        .replace(/[^A-Z0-9-_]/g, '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\r?\n|\r/g, '')
    },
    async checkAllocation (data) {
      const resp = await this.$engine.services.payments.getInvoices({ InvoiceIdentifier: data })

      return (
        resp.data.Invoices.length === 0
      )
    },
    async checkAllocationClient (data) {
      const resp = await this.$engine.services.payments.getGroups({ Reference1: data })

      return (
        resp.data.references.length === 0
      )
    },
    generateClientNumber (){
      let clientNumber = 'CLI-' + Math.random().toString(20).substr(2, 8).toUpperCase()

      if (!this.checkAllocationClient(clientNumber)) return this.generateClientNumber()

      this.clientNumber = clientNumber
      return clientNumber
    },
    generateReference (){
      let reference = 'ORD-' + (Math.random().toString(20).substr(2, 6) + '-' + Math.random().toString(20).substr(2, 3)).toUpperCase()

      if (!this.checkAllocation(reference)) return this.generateReference()

      this.reference = reference
      return reference
    }
  }
}
</script>
<style scoped>

.drop-zone.hover {
  border-color: #155dfc;
  background-color: #eff6ff;
}

.download-btn{
  text-wrap: nowrap;
  background-color: #fff !important;
  border-color: #8ec5ff !important;
  display: flex !important;
  gap: 1rem;
}

.drop-zone{
  row-gap: 1rem;
  border: 2px dashed #d1d5dc;
  background-color: #f8f8f8;
  border-radius: 0.625rem;
  padding: calc(.25rem * 12);
  display: grid !important;
  justify-content: center !important;
  align-items: center !important;
}

.drop-zone>i{
  display: flex;
  justify-content: center;
  font-size: calc(.25rem * 12);
  font-weight: bold;
  color: #99a1af;
  margin: 1rem 0rem;
}

.drop-zone>span{
  display: flex;
  justify-content: center;
}

.drop-zone:hover{
  border-color: #99a1af;
}

.drop-zone>div{
  display: flex;
  justify-content: center;
}

.information-container{
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: calc(.25rem * 4);
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: .625rem;
  margin: 1rem 0rem;
}

.information-container>i {
  color: #155dfc;
  font-size: calc(.25rem * 5);
}

.information-container>div{
  display: grid;
}

.information-container>div>span:first-child{
  color: #1c398e;
  font-weight: bold;
}

.information-container>div>span:last-child{
  color: #1d4ed8;
}

.modal-container{
  width: 100vw;
  height: 100vh;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 5000;
  overflow: hidden;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content .filter-item {
  margin: 1rem 0rem;
}

.modal-content>h1{
  font-weight: bold;
  font-size: 1.3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #000;
}

.modal-content>h1>i{
  cursor: pointer;
}

.modal-content>input{
  margin-bottom: 1.5rem;
}

.modal-content>span:first-of-type{
  color: #717182;
  margin-bottom: 1rem;
}

.modal-content>span:last-of-type{
  color: #000;
  font-weight: bold;
}
</style>